import { bandPercentiles } from "../lib/percentile";
import type { TrendSeries, TrendSeriesResult } from "../types";
import { fetchTrendSeries, type TrendSeriesRequest } from "./fetchTrendSeries";

/**
 * Loaded series for the Trends page, one entry per display_id and variant.
 *
 * Each series is its own GET, sent as soon as a card asks for it, so a trend
 * with six series makes six requests in parallel and draws each line when its
 * response lands. Asking for a series while it's in flight waits on the same
 * request.
 *
 * Data is fresh for 10 minutes, like the gauges' series cache. After that the
 * next load refreshes it in the background, and the old line stays on show
 * until the new data lands.
 *
 * A first load that fails is stored so the card shows its error state, but it
 * isn't final: the next `loadTrendSeries` for that series tries again. A failed
 * refresh keeps the data already on show and tries again a minute later.
 *
 * A shared link's series are kept apart from the app's, under the link's slug:
 * the link loads at its own access, which can differ from the viewer's.
 *
 * Everything is dropped when the viewer's access changes (`clearTrendSeriesCache`):
 * data and errors loaded on the old access would otherwise stay on show. Pages
 * reload their series when the generation moves on.
 */

type Fetcher = (request: TrendSeriesRequest) => Promise<TrendSeries>;

export const TREND_SERIES_TTL_MS = 10 * 60 * 1000;
const RETRY_AFTER_MS = 60 * 1000;

let results: ReadonlyMap<string, TrendSeriesResult> = new Map();
// When each loaded series was fetched. Failures have no entry.
const fetchedAt = new Map<string, number>();
const inFlight = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();
// Moves on with every clear, so answers to requests sent before it are dropped.
let generation = 0;

/** A series' cache key, under the shared link it loads through, if any. */
export const trendSeriesKey = ({ displayId, variant }: TrendSeriesRequest, slug?: string | null) =>
  slug ? `${displayId}|${variant}|link:${slug}` : `${displayId}|${variant}`;

const publish = (key: string, result: TrendSeriesResult) => {
  // A new map on every change, so React sees a new snapshot.
  const next = new Map(results);
  next.set(key, result);
  results = next;
  listeners.forEach((listener) => listener());
};

export const loadingResult = ({ displayId, variant }: TrendSeriesRequest): TrendSeriesResult => ({
  displayId,
  variant,
  data: null,
  percentiles: null,
  isLoading: true,
  error: null,
});

export const subscribeTrendSeries = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getTrendSeriesSnapshot = () => results;

/** How many times the cache has been cleared; a page reloads its series when it changes. */
export const getTrendSeriesGeneration = () => generation;

const isFresh = (key: string) => Date.now() - (fetchedAt.get(key) ?? -Infinity) < TREND_SERIES_TTL_MS;

/**
 * Starts loading a series unless it's fresh or already loading: a first load,
 * a retry after a failure, or a refresh once the data is 10 minutes old.
 * Never rejects.
 */
export function loadTrendSeries(
  request: TrendSeriesRequest,
  fetcher: Fetcher = fetchTrendSeries,
  slug: string | null = null,
): Promise<void> {
  const key = trendSeriesKey(request, slug);
  const pending = inFlight.get(key);
  if (pending) return pending;
  const existing = results.get(key);
  const hasData = !!existing?.data;
  if (hasData && isFresh(key)) return Promise.resolve();

  const { displayId, variant } = request;
  const sentIn = generation;
  // A refresh keeps the current line on show; anything else shows loading.
  if (!hasData) publish(key, loadingResult(request));
  const promise = fetcher(request)
    .then(
      (data) => {
        if (sentIn !== generation) return;
        fetchedAt.set(key, Date.now());
        publish(key, {
          displayId,
          variant,
          data,
          // The data service's own placement, else worked out from the bands.
          percentiles: data.gaugePercentile ?? bandPercentiles(data),
          isLoading: false,
          error: null,
        });
      },
      (error: unknown) => {
        if (sentIn !== generation) return;
        if (hasData) {
          // Keep the data on show and try again in a minute.
          fetchedAt.set(key, Date.now() - TREND_SERIES_TTL_MS + RETRY_AFTER_MS);
          return;
        }
        publish(key, {
          displayId,
          variant,
          data: null,
          percentiles: null,
          isLoading: false,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      },
    )
    .finally(() => {
      if (inFlight.get(key) === promise) inFlight.delete(key);
    });
  inFlight.set(key, promise);
  return promise;
}

/**
 * `loadTrendSeries` for series that already have data: refreshes them once
 * they're stale, and leaves failed or unloaded series alone.
 */
export function refreshTrendSeries(
  request: TrendSeriesRequest,
  fetcher: Fetcher = fetchTrendSeries,
  slug: string | null = null,
): Promise<void> {
  if (!results.get(trendSeriesKey(request, slug))?.data) return Promise.resolve();
  return loadTrendSeries(request, fetcher, slug);
}

/** The payload's display name for a series, from any variant that has loaded. */
export function loadedDisplayName(snapshot: ReadonlyMap<string, TrendSeriesResult>, displayId: string) {
  for (const result of Array.from(snapshot.values())) {
    if (result.displayId === displayId && result.data?.display_name) return result.data.display_name;
  }
  return undefined;
}

/**
 * Forget everything loaded, and any answer still on its way. For when the
 * viewer's access changes: they signed in or out, or their roles did.
 */
export function clearTrendSeriesCache() {
  generation += 1;
  results = new Map();
  fetchedAt.clear();
  inFlight.clear();
  listeners.forEach((listener) => listener());
}
